/**
 * `GraphSavedViewsBar` — the save / apply / share toolbar for any graph surface.
 *
 * One reusable control that composes the two lower packages the dashboard already
 * depends on: it drives a `@authe/saved-views` graph controller (`useGraphViews`)
 * and snapshots a `@authe/tx-graph` `GraphShareState`. Drop it next to any
 * `GraphCanvas` to let the user:
 *
 *  - **Save as view** — snapshot the graph currently on screen as a personal or
 *    tenant-shared saved view (`SaveCurrentViewDialog` → `controller.save`).
 *  - **Apply** — switch to / reset from a saved view via the `SavedViewsMenu`.
 *    Applying replaces the host's native/live graph with the saved snapshot; the
 *    menu's *Reset* clears it (`onApplyGraph(null)`) so the host shows live again.
 *  - **Share** — mint tokenized share links for the active saved view
 *    (`ShareViewDialog`), with audience / password / expiry / max-views.
 *
 * The bar owns the saved-views state; the host only supplies the graph to
 * snapshot (`currentGraph`) and reacts to applies (`onApplyGraph`) — keeping the
 * saved-views and tx-graph packages decoupled (the dashboard composes them).
 *
 * Two kinds of host reach it. An ELEMENT surface (the overview activity panel)
 * draws node/edge arrays, so applying a view swaps its payload and it mounts
 * this by hand. A SCENE surface gets the same bar through
 * `DashboardGraphDefaults.renderViewsBar` and never mounts one. A transaction's
 * Flow tab used to be both — a scene when one was persisted, a hand-built
 * address graph otherwise, each with its own saved-view list, and a view saved
 * on one inapplicable on the other. It is a scene surface now.
 */
import * as React from "react";
import { Share2 } from "lucide-react";
import {
  useGraphViews,
  SaveCurrentViewDialog,
  SavedViewsMenu,
  ShareViewDialog,
  type AccessWarning,
  type SavedView,
  type ViewScope,
  type ViewState,
} from "@authe/saved-views";
import { GraphExportMenu, type GraphShareState } from "@authe/tx-graph";
import { Button, cn } from "@authe/ui-core";
import { useStorageIdentity } from "@/app/storageIdentity";
import { useRecordVisibility } from "@/app/recordVisibility";
import {
  LIVE_SHARES_SERVABLE,
  useSavedViewPermissions,
  useShareTeamOptions,
} from "@/app/DashboardSavedViews";
import { useSavedViewsSource } from "@/api/adapters";
import { RESOURCES, ROUTES } from "@/config";
import { graphViewSaveInput, readGraphState } from "./graphViewState";

export interface GraphSavedViewsBarProps {
  /** Saved-views "table" the graph views live under. Default `RESOURCES.graphs`. */
  tableId?: string;
  /**
   * Which graph surface these views belong to (`transactions.activity`,
   * `investigation.<id>.graph`). Scopes the list, because a view saved on one
   * case's graph is meaningless on another's — and an unscoped list mixing them
   * is worse than no list.
   */
  graphId?: string;
  /** Snapshot of the graph currently shown — frozen into a view when the user saves. */
  currentGraph: GraphShareState;
  /** Render a saved view's graph (`null` = reset to the host's native/live graph). */
  onApplyGraph: (graph: GraphShareState | null) => void;
  /** Surface a shared view's access warnings to the host (optional). */
  onWarnings?: (warnings: AccessWarning[]) => void;
  /** Apply the default/last view on mount — the dedicated /graphs page wants this. */
  autoApplyDefault?: boolean;
  /** Disable saving (e.g. nothing on the canvas yet). */
  saveDisabled?: boolean;
  /**
   * Offer the export menu beside Share. Default true.
   *
   * This bar is the toolbar of the ELEMENT-level surfaces — the ones that draw
   * node/edge payloads rather than a `GraphScene`, and so mount it by hand. A
   * scene surface gets export from `SceneGraphView`'s own toolbar instead
   * (`GraphDefaults.showExport`, on by default), so the views SLOT passes false
   * here rather than putting two identical menus in the same pill.
   */
  showExport?: boolean;
  /**
   * The element wrapping the canvas, so export can offer the PNG. Optional:
   * without it the picture entry is simply absent and the data exports still
   * work — `GraphCanvas` takes a `containerRef` if you want to supply one.
   */
  canvasRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

export function GraphSavedViewsBar({
  tableId = RESOURCES.graphs,
  graphId,
  currentGraph,
  onApplyGraph,
  onWarnings,
  autoApplyDefault = false,
  saveDisabled = false,
  showExport = true,
  canvasRef,
  className,
}: GraphSavedViewsBarProps): React.ReactElement {
  const recordDefaults = useRecordVisibility();
  const source = useSavedViewsSource();
  // Same four questions as every other saved-views surface, answered in one
  // place (`useSavedViewPermissions`) rather than re-derived per bar.
  const perms = useSavedViewPermissions();
  // Only the options here: this bar renders no chrome of its own, and the
  // failed-read notice belongs beside the dialog (see `GraphsPage`).
  const { options: teamOptions } = useShareTeamOptions();
  const scopedTableId = graphId ? `${tableId}:${graphId}` : tableId;
  const identity = useStorageIdentity();
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);

  const onWarningsRef = React.useRef(onWarnings);
  onWarningsRef.current = onWarnings;
  const onApplyGraphRef = React.useRef(onApplyGraph);
  onApplyGraphRef.current = onApplyGraph;

  const onApply = React.useCallback((state: ViewState, view: SavedView | null) => {
    const next = readGraphState(state);
    onApplyGraphRef.current(view ? next.graph : null);
    onWarningsRef.current?.(view ? next.warnings : []);
  }, []);

  const controller = useGraphViews({
    source,
    tableId: scopedTableId,
    onApply,
    autoLoad: autoApplyDefault,
    identity,
  });

  // When we don't auto-apply on mount, still populate the switcher so the user
  // can pick a view (refresh loads the list without applying anything).
  const refresh = controller.refresh;
  React.useEffect(() => {
    if (!autoApplyDefault) refresh();
  }, [autoApplyDefault, refresh]);

  const handleSave = React.useCallback(
    async (name: string, scope: ViewScope): Promise<void> => {
      const { state, serving } = graphViewSaveInput(currentGraph);
      await controller.save(name, state, scope, serving);
    },
    [controller, currentGraph],
  );

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <SavedViewsMenu
        controller={controller}
        onSaveCurrent={() => {
          if (!saveDisabled && perms.canPersonalize) setSaveOpen(true);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-2"
        disabled={!controller.activeViewId || !perms.canShare}
        onClick={() => setShareOpen(true)}
        title={
          !perms.canShare
            ? "You don't have permission to share saved views"
            : controller.activeViewId
              ? "Share this view"
              : "Save a view first to share it"
        }
      >
        <Share2 className="h-3.5 w-3.5" />
        Share
      </Button>

      {showExport ? (
        <GraphExportMenu
          graph={currentGraph}
          {...(canvasRef ? { containerRef: canvasRef } : {})}
          filenameBase={controller.activeView?.name ?? graphId ?? "graph"}
          exportContext={{
            tableId,
            ...(controller.activeViewId ? { viewId: controller.activeViewId } : {}),
          }}
          historyHref={ROUTES.settingsExports}
        />
      ) : null}

      <SaveCurrentViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        canShare={perms.canPublish}
        // Which of Personal / Shared the dialog opens on — the caller's
        // configured default rather than this dialog's hard-coded "user". They
        // can still change it per save; this only decides where it starts.
        defaultScope={recordDefaults("savedView").scope}
        onSave={handleSave}
      />
      <ShareViewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        source={source}
        viewId={controller.activeViewId}
        // Both halves — see `LIVE_SHARES_SERVABLE`.
        allowDynamic={LIVE_SHARES_SERVABLE && Boolean(controller.activeView?.dynamicSeed)}
        // The "A team" audience gates on team membership, and the app knows
        // the caller's teams — without this the picker falls back to a free-text
        // field and sharing with a team means pasting a raw team id.
        teamOptions={teamOptions}
      />
    </div>
  );
}
